import React from 'react';
import { MailOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedModal from '@portal/components/AdvancedModal/AdvancedModal';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import useReduxState from '@portal/hooks/useReduxState';
import { PendingPixKeyPortability } from '@portal/models/module';
import { translate } from '@portal/services/i18n';
import { formatPixKeyByType } from '@portal/services/masks';
import { PixType } from '@portal/enum/pix';

import './PixPortabilityModal.less';
import { formatDateYearMonthDay } from '@portal/utils/format';

export interface PixPortabilityModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  pendingPortability: PendingPixKeyPortability | null;
  onKeepKey: () => void;
  onUseInOtherAccount: () => void;
}

const PixPortabilityModal = ({
  visible,
  setVisible,
  pendingPortability,
  onKeepKey,
  onUseInOtherAccount,
}: PixPortabilityModalProps) => {
  const {
    theme: { currentTheme },
  } = useReduxState();

  if (!pendingPortability) {
    return null;
  }

  const keyDisplay =
    formatPixKeyByType(pendingPortability.key, pendingPortability.type) ??
    pendingPortability.key;
  const dueDateFormatted = formatDateYearMonthDay(
    pendingPortability.limitDate ?? pendingPortability.dueDate ?? ''
  );

  const isReivindication = pendingPortability.isReivindication;

  const titleText = isReivindication
    ? translate(
        'COMPONENTS.PIX_PORTABILITY_MODAL.PORTABILITY_CHOICE.TITLE_REIVINDICATION'
      )
    : translate('COMPONENTS.PIX_PORTABILITY_MODAL.PORTABILITY_CHOICE.TITLE');

  const descriptionText = isReivindication
    ? translate(
        'COMPONENTS.PIX_PORTABILITY_MODAL.PORTABILITY_CHOICE.DESCRIPTION_REIVINDICATION'
      )
    : dueDateFormatted
    ? translate(
        'COMPONENTS.PIX_PORTABILITY_MODAL.PORTABILITY_CHOICE.DESCRIPTION_WITH_DATE',
        {
          dueDate: dueDateFormatted,
        }
      )
    : translate(
        'COMPONENTS.PIX_PORTABILITY_MODAL.PORTABILITY_CHOICE.DESCRIPTION'
      );

  const getKeyIcon = () => {
    const iconSize = 24;
    const iconColor = currentTheme?.primaryColor ?? undefined;
    if (pendingPortability.type === PixType.EMAIL) {
      return (
        <MailOutlined
          style={
            iconColor ? { color: iconColor, fontSize: iconSize } : undefined
          }
        />
      );
    }
    if (pendingPortability.type === PixType.PHONE) {
      return (
        <PhoneOutlined
          style={
            iconColor ? { color: iconColor, fontSize: iconSize } : undefined
          }
        />
      );
    }
    return (
      <UserOutlined
        style={iconColor ? { color: iconColor, fontSize: iconSize } : undefined}
      />
    );
  };

  const displayKey =
    keyDisplay.length > 40 ? `${keyDisplay.substring(0, 35)}...` : keyDisplay;

  return (
    <AdvancedModal
      visible={visible}
      onClose={() => setVisible(false)}
      title={titleText}
      size="small"
      autoHeight
    >
      <div className="pix-portability-choice-modal">
        <div className="pix-portability-choice-modal__key-block">
          <div className="pix-portability-choice-modal__key-row align-center">
            <span className="pix-portability-choice-modal__key-icon">
              {getKeyIcon()}
            </span>
            <ThemedText
              variant="body"
              color="default"
              className="pix-portability-choice-modal__key-text"
            >
              {displayKey}
            </ThemedText>
          </div>
        </div>
        <ThemedText
          variant="body"
          color="gray"
          align="center"
          className="pix-portability-choice-modal__description"
        >
          {descriptionText}
        </ThemedText>
        {dueDateFormatted ? (
          <ThemedText
            variant="body"
            color="default"
            align="center"
            className="pix-portability-choice-modal__limit-date"
          >
            <strong>
              {translate(
                'COMPONENTS.PIX_PORTABILITY_MODAL.PORTABILITY_CHOICE.LIMIT_DATE',
                { date: dueDateFormatted }
              )}
            </strong>
          </ThemedText>
        ) : null}
        <div className="pix-portability-choice-modal__buttons">
          <AdvancedButton
            text={translate(
              'COMPONENTS.PIX_PORTABILITY_MODAL.PORTABILITY_CHOICE.KEEP_KEY'
            )}
            onClick={onKeepKey}
            variant="contained"
            color="primary"
            fullWidth
          />
          <AdvancedButton
            text={translate(
              'COMPONENTS.PIX_PORTABILITY_MODAL.PORTABILITY_CHOICE.USE_IN_OTHER_ACCOUNT'
            )}
            onClick={onUseInOtherAccount}
            variant="outlined"
            color="primary"
            fullWidth
          />
        </div>
      </div>
    </AdvancedModal>
  );
};

export default PixPortabilityModal;
